import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, KpiCard, ScostamentoRischio, SectionTitle } from "@/components/Kpi";
import { MESI, eurShort, num } from "@/lib/format";
import { COLORS, ComparisonChart, PieBreakdown } from "@/components/Charts";
import { ConfrontoYtdAutonomo } from "@/components/corto/ConfrontoYtd";
import { FILTRI_CORTO_VUOTI, FiltriCorto } from "@/components/corto/FiltriCorto";
import { ApprovatoDaLiquidareModal } from "@/components/corto/ApprovatoDaLiquidareModal";
import BudgetGauge from "@/components/BudgetGauge";
import { api } from "@/lib/api";
import { ContoCartaChart } from "@/components/corto/ContoCartaChart";

const PRODOTTI = [["non_garantito", "Non garantito"], ["garantito", "Garantito"],
  ["vivibanca", "Vivibanca"]];
const somma = (serie, k) => (serie || []).reduce((s, r) => s + (r[k] || 0), 0);
const quota = (parte, tot) => (tot ? `${num(parte / tot * 100, 1)}% del totale` : "n/d");

export default function AgenziaCorto() {
  const { slug } = useParams();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState("");
  const [statoYtd, setStatoYtd] = useState({});
  const [approvato, setApprovato] = useState(false);
  const [filtri, setFiltri] = useState({ ...FILTRI_CORTO_VUOTI });
  const [risorse, setRisorse] = useState([]);
  const qsFiltri = new URLSearchParams(Object.entries(filtri).filter(([, v]) => v)).toString();

  useEffect(() => { setFiltri({ ...FILTRI_CORTO_VUOTI }); }, [slug]);

  useEffect(() => {
    setErrore("");
    api.get(`/corto/agenzie/${slug}${qsFiltri ? `?${qsFiltri}` : ""}`).then((r) => setDati(r.data))
      .catch(() => setErrore("Agenzia non trovata"));
  }, [slug, qsFiltri]);

  useEffect(() => {
    if (!dati?.agenzia) return;
    api.get(`/corto/risorse?agenzia=${encodeURIComponent(dati.agenzia)}`)
      .then((r) => setRisorse(r.data.rows || [])).catch(() => setRisorse([]));
  }, [dati?.agenzia]);

  if (errore) return <Card className="p-8 text-sm text-slate-500" data-testid="corto-agenzia-errore">{errore}</Card>;
  if (!dati) return <div className="text-sm text-slate-500" data-testid="corto-agenzia-loading">Caricamento…</div>;

  const car = dati.blocco_caricato;
  const liq = dati.blocco_liquidato;
  const t = dati.totali || {};
  const p = dati.previsioni || {};
  const meseLbl = p.mese ? MESI[p.mese - 1].toLowerCase() : "mese";

  return (
    <div className="space-y-8" data-testid="corto-agenzia-page">
      <SectionTitle sub={`Prestiti personali · ${dati.anno} · ${dati.agenzie_totali} agenzie`}
        right={<div className="flex items-center gap-3">
          <a href={`/api/corto/report/agenzia/${dati.slug}.pdf?anno=${dati.anno}${
            qsFiltri ? `&${qsFiltri}` : ""}${
            statoYtd.ytd_dal ? `&ytd_dal=${statoYtd.ytd_dal}` : ""}${
            statoYtd.ytd_al ? `&ytd_al=${statoYtd.ytd_al}` : ""}${
            statoYtd.ytd_prodotto ? `&ytd_prodotto=${statoYtd.ytd_prodotto}` : ""}`}
            data-testid="btn-pdf-corto-agenzia"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm
              hover:bg-[#001480] transition-colors duration-200">Scarica PDF</a>
          <Link to="/canale-corto" data-testid="btn-torna-dashboard-corto"
            className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500
              hover:text-[#C8102E] transition-colors duration-200">Torna alla dashboard</Link>
        </div>}>
        {dati.agenzia}
      </SectionTitle>

      <FiltriCorto valori={filtri} onChange={setFiltri} agenzie={[]} risorse={risorse}
        agenziaFissa={dati.agenzia} />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="corto-agenzia-kpi-totali">
        <KpiCard label="Caricato totale" value={t.importo_caricato ?? car.totale} kind="eur" accent="caricato"
          sub={`${num(t.pratiche_caricate || 0)} pratiche`} testId="corto-agenzia-kpi-caricato" />
        <KpiCard label="Caricato non garantito" value={t.caricato_non_garantito} kind="eur"
          accent="caricato" sub={quota(t.caricato_non_garantito, t.importo_caricato)} testId="corto-agenzia-kpi-caricato-ng" />
        <KpiCard label="Caricato garantito" value={t.caricato_garantito} kind="eur"
          accent="caricato" sub={quota(t.caricato_garantito, t.importo_caricato)} testId="corto-agenzia-kpi-caricato-g" />
        <KpiCard label="Caricato Vivibanca" value={t.caricato_vivibanca} kind="eur"
          accent="caricato" sub={quota(t.caricato_vivibanca, t.importo_caricato)} testId="corto-agenzia-kpi-caricato-vb" />

        <KpiCard label="Liquidato totale" value={t.importo_liquidato ?? liq.totale} kind="eur" accent="liquidato"
          sub={`${num(t.pratiche_liquidate || 0)} pratiche`} testId="corto-agenzia-kpi-liquidato" />
        <KpiCard label="Liquidato non garantito" value={t.liquidato_non_garantito} kind="eur"
          accent="liquidato" sub={quota(t.liquidato_non_garantito, t.importo_liquidato)} testId="corto-agenzia-kpi-liquidato-ng" />
        <KpiCard label="Liquidato garantito" value={t.liquidato_garantito} kind="eur"
          accent="liquidato" sub={quota(t.liquidato_garantito, t.importo_liquidato)} testId="corto-agenzia-kpi-liquidato-g" />
        <KpiCard label="Liquidato Vivibanca" value={t.liquidato_vivibanca} kind="eur"
          accent="liquidato" sub={quota(t.liquidato_vivibanca, t.importo_liquidato)} testId="corto-agenzia-kpi-liquidato-vb" />

        <KpiCard label="% raggiungimento budget liquidato" value={p.raggiungimento_perc} kind="quota"
          accent="previsione" sub={`Budget ${eurShort(p.budget_liquidato)}`}
          testId="corto-agenzia-kpi-budget-liquidato" />
        <KpiCard label="Previsione chiusura anno · liquidato" value={p.proiezione_anno} kind="eurShort"
          accent="previsione" variance={p.scostamento_perc} sub="scostamento vs budget"
          testId="corto-agenzia-kpi-previsione-anno-liquidato" />
        <KpiCard label={`Previsione chiusura ${meseLbl} · pratiche liquidate`} value={p.proiezione_mese_pratiche}
          accent="previsione" testId="corto-agenzia-kpi-previsione-mese-liquidate" />
        <KpiCard label={`Previsione chiusura ${meseLbl} · importo liquidato`} value={p.proiezione_mese_importo}
          kind="eurShort" accent="previsione" testId="corto-agenzia-kpi-previsione-mese-liquidato" />

        <KpiCard label="Premi assicurativi (CPI + Bundle)" value={dati.premi_totale} kind="eur"
          accent="sky" sub="importi liquidati" testId="corto-agenzia-premi-progressivo" />
        <KpiCard label="Redditività" value={dati.redditivita_totale} kind="quota" accent="positivo"
          testId="corto-agenzia-kpi-redditivita" />
        <KpiCard label="Rischio" value={dati.rischio} kind="quota"
          accent={dati.rischio != null && dati.rischio_target != null ? (dati.rischio <= dati.rischio_target ? "positivo" : "negativo") : "slate"}
          sub={<ScostamentoRischio rischio={dati.rischio} target={dati.rischio_target} />} testId="corto-agenzia-kpi-rischio" />
        <KpiCard label="Approvato da liquidare" value={dati.approvato_da_liquidare?.importo} kind="eur"
          accent="warning" sub={`${dati.approvato_da_liquidare?.pratiche || 0} pratiche approvate`}
          onClick={() => setApprovato(true)} testId="corto-agenzia-kpi-approvato-da-liquidare" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="corto-agenzia-kpi-rete">
        <KpiCard label="Quota caricato sulla rete" value={car.quota_perc} kind="quota" accent="sky"
          testId="corto-agenzia-kpi-quota" />
        <KpiCard label="Posizione caricato" value={car.posizione} accent="positivo"
          sub={`su ${dati.agenzie_totali} agenzie`} testId="corto-agenzia-kpi-posizione" />
        <KpiCard label="Quota liquidato sulla rete" value={liq.quota_perc} kind="quota" accent="sky"
          testId="corto-agenzia-kpi-quota-liquidato" />
        <KpiCard label="Posizione liquidato" value={liq.posizione} accent="positivo"
          sub={`su ${dati.agenzie_totali} agenzie`} testId="corto-agenzia-kpi-posizione-liquidato" />
      </div>
      {approvato && <ApprovatoDaLiquidareModal anno={dati.anno} agenzia={dati.agenzia}
        risorsa={filtri.risorsa || null} onClose={() => setApprovato(false)} />}

      {dati.confronto ? (
        <div className="space-y-4" data-testid="corto-agenzia-confronto">
          <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
            Confronto {dati.confronto.anno} vs {dati.confronto.anno_precedente}
          </div>
          {dati.confronto.ordine.map((campo) => (
            <div key={campo} className="space-y-4">
            <ComparisonChart height={320}
              testId={`corto-agenzia-confronto-${campo}`}
              title={`${dati.confronto.titoli[campo]} · ${dati.confronto.anno_precedente} vs ${dati.confronto.anno}`}
              labelFormat={campo.startsWith("pezzi") ? "int" : "compact"}
              data={dati.confronto.serie[campo]}
              keys={[{ key: "precedente", name: String(dati.confronto.anno_precedente),
                color: COLORS.grigio },
              { key: "corrente", name: String(dati.confronto.anno),
                color: campo.includes("liquidato") ? COLORS.liquidato : COLORS.caricato }]} />
            {campo === "liquidato" ? (
              <ConfrontoYtdAutonomo slug={slug} testId="corto-agenzia-ytd-liquidato"
                extra={{ risorsa: filtri.risorsa }} onStato={setStatoYtd}
                titolo={`Liquidato progressivo · ${dati.agenzia}`} />
            ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ComparisonChart testId="corto-agenzia-chart" height={320}
          title="Caricato per mese vs media della rete" data={dati.serie}
          keys={[{ key: "caricato", name: dati.agenzia, color: COLORS.caricato },
            { key: "media_rete", name: "Media rete", color: COLORS.grigio }]} />
        <ComparisonChart testId="corto-agenzia-chart-liquidato" height={320}
          title="Liquidato per mese vs media della rete" data={dati.serie}
          keys={[{ key: "liquidato", name: dati.agenzia, color: COLORS.liquidato },
            { key: "media_rete_liquidato", name: "Media rete", color: COLORS.grigio }]} />
      </div>

      {dati.ticket?.length ? (
        <ComparisonChart testId="corto-agenzia-chart-ticket" height={320}
          title="Ticket medio · caricato, liquidato, netto erogato"
          type="line" labelFormat="int" data={dati.ticket}
          keys={[{ key: "caricato", name: "Caricato", color: COLORS.caricato },
            { key: "liquidato", name: "Liquidato", color: COLORS.liquidato },
            { key: "netto_erogato", name: "Netto erogato", color: COLORS.pos }]} />
      ) : null}

      {dati.premi?.length ? (
        <div data-testid="corto-agenzia-premi">
          <ComparisonChart testId="corto-agenzia-chart-premi" height={320}
            title="Premi assicurativi per mese" data={dati.premi}
            keys={[{ key: "totale", name: "Premi", color: COLORS.curr }]} />
        </div>
      ) : null}

      {dati.stati?.length ? (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <div className="xl:col-span-2">
            <ComparisonChart testId="corto-agenzia-chart-stati" height={320}
              title="Esiti pratiche per mese" labelFormat="int" data={dati.stati}
              keys={[{ key: "accettata", name: "Accettate", color: COLORS.pos, stackId: "s" },
                { key: "attesa", name: "In attesa", color: COLORS.warn, stackId: "s" },
                { key: "respinta", name: "Respinte", color: "#DC2626", stackId: "s" },
                { key: "ritirata", name: "Ritirate", color: COLORS.grigio, stackId: "s" },
                { key: "rinuncia", name: "Rinunce", color: "#A855F7", stackId: "s" }]} />
          </div>
          <PieBreakdown testId="corto-agenzia-pie-caricato" title="Caricato per prodotto"
            data={PRODOTTI.map(([k, nome]) => ({ name: nome, value: somma(dati.prodotti_caricato, k) }))} />
          <PieBreakdown testId="corto-agenzia-pie-liquidato" title="Liquidato per prodotto"
            data={PRODOTTI.map(([k, nome]) => ({ name: nome, value: somma(dati.prodotti_liquidato, k) }))} />
        </div>
      ) : null}


      <ContoCartaChart anno={dati.anno} agenzia={dati.agenzia} />

      {dati.tachimetri?.length ? (
        <div data-testid="corto-agenzia-tachimetri">
          <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
            Obiettivi {dati.anno} · anno, 3° quarter e mese in corso
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {dati.tachimetri.map((k) => (
              <BudgetGauge key={k.label} titolo={k.label} tipo="LIQUIDATO"
                sottotitolo={k.periodo === "mese" ? "liquidato del mese vs budget"
                  : `proiezione ${k.periodo} vs budget agenzia`}
                budget={k.budget}
                proiezione={k.valore} scostamentoPerc={k.scostamento_perc}
                testId={`corto-agenzia-tachimetro-${k.periodo}-${k.categoria}`} />
            ))}
          </div>
        </div>
      ) : null}

    </div>
  );
}
